import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TestimonialComponent } from '../../../types/builder';
import { useBuilderStore } from '../../../store/builderStore';
import { ColorPicker } from '../../Common/ColorPicker';
import { Upload, Loader } from 'lucide-react';
import toast from 'react-hot-toast';

interface TestimonialSettingsProps {
  component: TestimonialComponent;
}

export const TestimonialSettings: React.FC<TestimonialSettingsProps> = ({ component }) => {
  const { t } = useTranslation('builder');
  const { updateComponent } = useBuilderStore();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);

  const handleUpdate = (updates: Partial<TestimonialComponent['settings']>) => {
    updateComponent(component.id, {
      settings: { ...component.settings, ...updates },
    });
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error(t('settings.testimonial.imageOnly'));
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error(t('settings.testimonial.imageTooLarge5MB'));
      return;
    }

    setIsUploading(true);

    try {
      const formData = new FormData();
      formData.append('avatar', file);

      const token = localStorage.getItem('token');
      const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

      const response = await fetch(`${API_URL}/upload/avatar`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Upload failed');
      }

      const data = await response.json();
      handleUpdate({ avatarUrl: data.url });
      toast.success(t('settings.testimonial.uploadAvatarSuccess'));
    } catch (error: any) {
      console.error('Upload error:', error);
      toast.error(error.message || t('settings.testimonial.uploadAvatarFailed'));
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium mb-2">{t('settings.testimonial.avatarImage')}</label>

        {/* Image preview */}
        {component.settings.avatarUrl && (
          <div className="mb-3">
            <img
              src={component.settings.avatarUrl}
              alt={t('settings.testimonial.avatarPreviewAlt')}
              className="w-20 h-20 rounded-full object-cover border-2 border-gray-300"
              onError={(e) => {
                (e.target as HTMLImageElement).src = 'https://ui-avatars.com/api/?name=Avatar&size=200&background=667eea&color=fff';
              }}
            />
          </div>
        )}

        {/* Upload button */}
        <div className="flex gap-2 mb-2">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileUpload}
            className="hidden"
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
          >
            {isUploading ? (
              <>
                <Loader className="w-4 h-4 animate-spin" />
                {t('settings.common.uploading')}
              </>
            ) : (
              <>
                <Upload className="w-4 h-4" />
                {t('settings.common.uploadImage')}
              </>
            )}
          </button>
          <span className="text-xs text-gray-400 flex items-center">
            {t('settings.testimonial.avatarHint')}
          </span>
        </div>
        <p className="text-xs text-gray-500 mt-1">
          {t('settings.testimonial.avatarFormatHint')}
        </p>

        {/* URL input (alternative) */}
        <div className="mt-2">
          <label className="block text-xs text-gray-400 mb-1">{t('settings.testimonial.orImageUrl')}</label>
          <input
            type="text"
            value={component.settings.avatarUrl}
            onChange={(e) => handleUpdate({ avatarUrl: e.target.value })}
            className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 break-all"
            placeholder={t('settings.testimonial.imageUrlPlaceholder')}
            title={component.settings.avatarUrl}
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">{t('settings.common.name')}</label>
        <input
          type="text"
          value={component.settings.name}
          onChange={(e) => handleUpdate({ name: e.target.value })}
          className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">{t('settings.testimonial.role')}</label>
        <input
          type="text"
          value={component.settings.role}
          onChange={(e) => handleUpdate({ role: e.target.value })}
          className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">{t('settings.common.rating')}</label>
        <input
          type="range"
          min="1"
          max="5"
          step="1"
          value={component.settings.rating}
          onChange={(e) => handleUpdate({ rating: parseInt(e.target.value) })}
          className="w-full"
        />
        <div className="text-xs text-gray-400 mt-1">
          {t('settings.testimonial.ratingStars', { count: component.settings.rating })}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">{t('settings.testimonial.review')}</label>
        <textarea
          value={component.settings.review}
          onChange={(e) => handleUpdate({ review: e.target.value })}
          className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
          rows={4}
        />
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">{t('settings.common.backgroundColor')}</label>
        <ColorPicker
          color={component.settings.backgroundColor}
          onChange={(color) => handleUpdate({ backgroundColor: color })}
        />
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">{t('settings.common.textColor')}</label>
        <ColorPicker
          color={component.settings.textColor}
          onChange={(color) => handleUpdate({ textColor: color })}
        />
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">{t('settings.common.starColor')}</label>
        <ColorPicker
          color={component.settings.starColor}
          onChange={(color) => handleUpdate({ starColor: color })}
        />
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">{t('settings.common.avatarSize')}</label>
        <input
          type="range"
          min="40"
          max="120"
          value={component.settings.avatarSize}
          onChange={(e) => handleUpdate({ avatarSize: parseInt(e.target.value) })}
          className="w-full"
        />
        <div className="text-xs text-gray-400 mt-1">{component.settings.avatarSize}px</div>
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">{t('settings.common.padding')}</label>
        <input
          type="range"
          min="16"
          max="60"
          value={component.settings.padding}
          onChange={(e) => handleUpdate({ padding: parseInt(e.target.value) })}
          className="w-full"
        />
        <div className="text-xs text-gray-400 mt-1">{component.settings.padding}px</div>
      </div>
    </div>
  );
};
